import React, { useEffect, useMemo, useRef, useState } from "react";
import Footer from "components/layout/footer";
import Skeleton from "react-loading-skeleton";
import expiryDateValidation, {
  checkBankManualFormOrPlaid,
  checkNewPaymentDetails,
  checkOldPaymentDetails,
  formatCurrency,
  formatExpirationDate,
  getBankName,
  getCardType,
  getGeneralDetailMarkers,
  getOperatingSystem,
  validateCard,
  validateEmail,
  decode,
  checkPhone,
  getMethodCode,
} from "utils/HelperFunctions";
import logo from "icons/smallLogo.svg";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { goToInvalid } from "utils/invalidRoute";
import {
  onSetFinalDisclosureLoading,
  onSetIsLogin,
  onSetShowDisclosure,
  useIsLogin,
  useIsSmallSidebar,
  useShowFinalDisclosure,
} from "store/slices/commonSlice";
import {
  clearAuthSlice,
  updateTokenExpiredStatus,
  useUserData,
} from "store/slices/authSlice";
import { TOKEN_STORAGE_KEY } from "store/api/axiosApi";
import { useProductLinkData } from "hooks/useProductLinkData";
import { usePaymentErrors } from "hooks/usePaymentErrors";
import { useCardState } from "hooks/useCardState";
import { useBankState } from "hooks/useBankState";
import { usePaymentProcess } from "hooks/usePaymentProcess";
import { useDefaultPaymentMethodView } from "hooks/useDefaultPaymentMethodView";
import { AddCircleIconBlue } from "utils/icon";
import PaymentProcessStep2 from "pages/payments/PaymentProcessStep2";
import PaymentMethodsComponent from "components/paymentMethod/PaymentMethodsComponent";
import { CardIcon } from "icons/payments/Card";
import { BankIconSm } from "icons/payments/BankIconSm";
import Button from "components/form/Button";
import moment from "moment";
import { PaypalIcon } from "icons/dashboard/paypal";
import { CashAppIcon } from "icons/dashboard/cashAppIcon";
import ProductLinkSuccess from "components/makePayment/ProductLinkSuccess";
import PaymentLinkProcessFailed from "components/actionCenter/PaymentLinkProcessFailed";
import { quickCheckoutGet } from "store/slices/actionSlice";
import CustomAmountInput from "components/ui-component/CustomAmountInput";
import OutsideClickHandler from "components/ui-component/OutsideClickHandler";
import { EditIcon } from "icons/payments/EditIcon";
import {
  buildQuickCheckoutReturnUrl,
  getQuickCheckoutFeeState,
  isQuickCheckoutEnabled,
  isQuickCheckoutPendingReply,
  readQuickCheckoutPayPalOrder,
  rememberQuickCheckoutReturnEmail,
  takeQuickCheckoutReturnEmail,
  saveQuickCheckoutPayPalOrder,
  isValidQuickCheckoutAmount,
  newQuickCheckoutAttemptId,
  QUICK_CHECKOUT_CONFIRMING_MESSAGE,
  quickCheckoutFailureReason,
  runQuickCheckoutPayment,
  QUICK_CHECKOUT_CUSTOM_CONV,
  QUICK_CHECKOUT_INTENT_BOUND_METHODS,
  toQuickCheckoutPayload,
} from "utils/quickCheckout";
import WirePaymentSuccess from "components/actionCenter/WirePaymentSuccess";
import QuickCheckoutPending from "components/makePayment/QuickCheckoutPending";
import { quickCheckoutPayment } from "store/services/paymentServices";
import {
  useLeadData,
  usePortalSettingsData,
} from "store/slices/dashboardSlice";
import {
  convFeeGet,
  createPaypalPaymentToken,
  createPaypalToken,
  paymentDownload,
  resetConvFee,
  updateCompanyDetails,
  updateLogoAndFavicon,
  updatePaymentPermission,
  wirePaymentProcess,
  resetDuplicateOrder,
  resetCheckDuplicatePayment,
  setIsPaymentProcessing,
  verifyPaymentsViaStax,
} from "store/slices/paymentsSlice";
import {
  clearPaymentMethodSlice,
  oldPaymentMethodsGet,
  useBankData,
  useCardData,
  useHasLoadedPaymentMethods,
  useIsDataLoading,
  useOldWalletData,
} from "store/slices/paymentMethodSlice";
import DisclosureInitial from "components/makePayment/DisclosureInitial";
import DisclosureFinal from "components/makePayment/DisclosureFinal";
import AmazonPay from "icons/dashboard/AmazonPay";
import { FormRef, PaymentGateway, SettingsValue } from "types/payments";
import { PaymentMethodList } from "types/payments";
import { RootState } from "store/store";
import BigInfoCircle from "icons/subscription/BigInfoCircle";
import { defaultPaymentMethodList } from "constants/paymentMethod";
import {
  invoiceDetailsSend,
} from "store/slices/subscriptionSlice";
import {
  VerifyStaxCardPaymentParams,
  VerifyStripeCardPaymentParams,
} from "types/agreement";
import { TestIdScope, useTestAttrsFactory } from "utils/testid";

/**
 * Public quick checkout (/:clusterId/quick-checkout): the customer enters an
 * amount and email and pays. Copied from the product link page (left
 * unchanged) with the products, upsell and subscription handling removed.
 */
const QuickCheckoutContent = () => {
  const dispatch = useAppDispatch();
  const testAttrs = useTestAttrsFactory();
  const accountLeadData = useLeadData();
  const userData = useUserData();
  const location = useLocation();
  const walletList = useOldWalletData();
  const searchParams = new URLSearchParams(location.search);
  const drawerRef = useRef<HTMLSpanElement>(null);
  const navigate = useNavigate();
  const isSmallSidebar = useIsSmallSidebar();
  const portalSettings = usePortalSettingsData();

  // Extracted state hooks
  const productLinkData = useProductLinkData();
  const paymentErrors = usePaymentErrors();
  const cardState = useCardState();
  const bankState = useBankState();
  const paymentProcess = usePaymentProcess();
  const setPaymentProcessIsAddNewCard = paymentProcess.setIsAddNewCard;

  // Regular state declarations
  const [initialDisclosureAccept, setInitialDisclosureAccept] =
    useState<boolean>(false);
  const showDetails = true;
  const [selectedState, setSelectedState] = useState<any>(undefined);
  const [isRingLoading, setIsRingLoading] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);

  const [isGoogleLoading, setIsGoogleLoading] = useState<boolean>(false);
  const [isStaxPayPalLoading, setIsStaxPayPalLoading] =
    useState<boolean>(false);
  const [showButtons, setShowButtons] = useState<boolean>(true);
  const [savedCardsEmail, setSavedCardsEmail] = useState<string>("");
  // Quick checkout payments are always one-time.
  const isRecurring = false;
  const [isManualForm, setIsManualForm] = useState<boolean>(true);
  const [paymentMethodList, setPaymentMethodList] = useState<
    PaymentMethodList[]
  >(defaultPaymentMethodList);
  const [paymentLogo, setPaymentLogo] = useState<any>(logo);
  const [selectedMethod, setSelectedMethod] = useState<
    "Card" | "Bank" | "PayPal" | "Cash App Pay" | string
  >("Card");
  const [tokenData, setTokenData] = useState<any>(null);
  const [amount, setAmount] = useState<any>(0);
  // Amount editing mirrors make payment: the draft only becomes the payment
  // amount (fee, Pay total, payment form) once saved on blur / outside click.
  const [isAmountEdit, setIsAmountEdit] = useState<boolean>(false);
  const [amountDraft, setAmountDraft] = useState<string>("");
  const [amountErr, setAmountErr] = useState<string | null>(null);
  const amountDraftRef = useRef<string>("");
  // The Cash App, Amazon Pay and bank forms hold a Stripe intent (or Plaid
  // link) made for one amount, and a mounted Stripe form cannot switch to a
  // new intent. So the payment form is rebuilt (new `version`) when the
  // amount changes after one of those methods has been opened. While only the
  // card view has been used, an amount change just refreshes the fee and the
  // Pay button; the wallet buttons and card fields stay as they are.
  const isIntentBoundMethod = QUICK_CHECKOUT_INTENT_BOUND_METHODS.includes(
    selectedMethod
  );
  const [paymentForm, setPaymentForm] = useState<{
    amount: any;
    version: number;
    intentMethodOpened: boolean;
  }>({ amount, version: 0, intentMethodOpened: false });
  const hasAmountChanged = paymentForm.amount !== amount;
  if (
    hasAmountChanged ||
    (isIntentBoundMethod && !paymentForm.intentMethodOpened)
  ) {
    setPaymentForm({
      amount,
      version:
        hasAmountChanged && paymentForm.intentMethodOpened
          ? paymentForm.version + 1
          : paymentForm.version,
      intentMethodOpened: hasAmountChanged ? isIntentBoundMethod : true,
    });
  }
  const [isAddNewCard, setIsAddNewCard] = useState<boolean>(false);
  const [isBTNLoading, setIsBTNLoading] = useState<boolean>(false);
  const [isScreenLoading, setIsScreenLoading] = useState<boolean>(false);
  // True from an amount / method change until its convenience fee is in.
  const [isFeeUpdating, setIsFeeUpdating] = useState<boolean>(false);

  const isDataLoading = useIsDataLoading();
  const hasLoadedPaymentMethods = useHasLoadedPaymentMethods();
  const { brandColor, isDark } = useAppSelector(
    (state: RootState) => state.dashboard
  );
  const cashAppRef = useRef<FormRef>(null);
  const bankRef = useRef<FormRef>(null);
  const cardRef = useRef<FormRef>(null);
  const amazonRef = useRef<FormRef>(null);
  const showFinalDisclosure = useShowFinalDisclosure();
  const isLogin = useIsLogin();

  const initialState = {
    filterParamValue: "",
    filterParam: "search",
    page: 1,
    state: "AL",
    country: "US",
    isrecurring: "N",
    agreementType: "new",
    type: "Amount",
    amount: "0.00",
    new_offer_type: "Amount",
    paymentPlan: { start_date: moment().format("MM/DD/YYYY") },
    selectedCard: {},
    selectedBank: {},
  };
  const cardList = useCardData();
  const bankList = useBankData();
  const stepperAnchorRef = useRef<HTMLDivElement>(null);
  const [dataPayment, setDataPayment] = useState<any>(initialState);

  const { paymentPermission, conFee, payPalOrder, companyDetails } =
    useAppSelector((state: RootState) => state.payment);
  const { clusterId: clusterSlug } = useParams();
  // The cluster comes from the `ci_` slug in /:clusterId/quick-checkout.
  // Kept as [unused, clusterId] so the copied handlers read it unchanged.
  const linkSlugParts: string[] = useMemo(
    () => ["", decode(clusterSlug || "", true)],
    [clusterSlug]
  );
  const linkClusterId = linkSlugParts[1];
  // Logged in: the payment goes to the logged-in account and saved methods
  // can be used. Guest: the API matches the customer by email.
  // One id per payment the customer starts. A double click, or Pay pressed
  // again after an unclear answer, sends the same id and the API returns the
  // order it already made instead of charging again. A new id once a result
  // is shown or the amount / method changes.
  const attemptIdRef = useRef<string>(newQuickCheckoutAttemptId());
  const renewAttemptId = () => {
    attemptIdRef.current = newQuickCheckoutAttemptId();
  };
  useEffect(() => {
    renewAttemptId();
  }, [amount, selectedMethod]);
  // Held for the return from a redirect payment, not put in its URL.
  useEffect(() => {
    if (paymentProcess.email && validateEmail(paymentProcess.email)) {
      rememberQuickCheckoutReturnEmail(paymentProcess.email);
    }
  }, [paymentProcess.email]);
  // Sends the payment through runQuickCheckoutPayment, so a payment that may
  // have gone through is shown as "being confirmed", never as failed. Only a
  // Stripe intent post is repeated automatically: the API recognises a
  // repeat of the same intent and returns the order it recorded.
  const submitCheckout = (payload: any) => {
    const body = {
      ...toQuickCheckoutPayload(
        payload,
        isLogin ? userData?.data?.accountData?.accountid : undefined
      ),
      qc_attempt_id: attemptIdRef.current,
    };
    return runQuickCheckoutPayment(quickCheckoutPayment, body, {
      canRetry: /^pi_[A-Za-z0-9]+$/.test(String(payload?.transaction_id || "")),
    });
  };
  // The payment may have gone through: no "try again". When the API saved
  // the order, its receipt is sent as for a paid one.
  const showPaymentPending = async (data: any, typedEmail?: string | null) => {
    paymentProcess.setPaymentProcess("pending");
    paymentErrors.setFailedReason(
      data?.message || QUICK_CHECKOUT_CONFIRMING_MESSAGE
    );
    if (data?.invoice_id) {
      await handleSendEmail({
        email: data?.email || typedEmail,
        order_id: data?.invoice_id,
        accountId: data?.accountid || userData?.data?.accountData?.accountid,
      });
    }
  };
  // 2-letter code of the card's US billing state. The fee shown, the Stripe
  // intent and the charge all use this one value, so they price the same.
  const cardFeeState = getQuickCheckoutFeeState(
    cardState.values?.x_country,
    cardState.values?.x_state,
    selectedState
  );

  const getQuickCheckoutDetails = async () => {
    if (!clusterSlug?.startsWith("ci_") || !/^\d+$/.test(linkClusterId || "")) {
      goToInvalid(navigate, "unavailable");
      return;
    }

    try {
      // A dropped connection is tried once more before the link is called
      // invalid (an answer from the API, e.g. switched off, is not retried).
      const loadDetails = () =>
        dispatch(quickCheckoutGet({ slug: clusterSlug })).unwrap();
      let res: any;
      try {
        res = await loadDetails();
      } catch (error: any) {
        if (!/network/i.test(String(error?.message || ""))) throw error;
        await new Promise((resolve) => setTimeout(resolve, 1500));
        res = await loadDetails();
      }

      if (
        !res?.body?.success ||
        !isQuickCheckoutEnabled(res.body.data?.quick_checkout_enabled)
      ) {
        goToInvalid(navigate, "unavailable");
        return;
      }

      const { paymentPermission, companyLogoDetails } = res.body.data || {};
      // Shape matches a product link without products so the payment-method
      // list and fee logic below work unchanged.
      productLinkData.setProductLinkData({
        paymentPermission,
        productLinkData: { productLinksPrices: [] },
      });
      // collect_address "Y" matches make payment: the card forms then show and
      // require billing address whenever the portal's cc_billing_address is on.
      productLinkData.setProdPermission({
        collect_address: "Y",
        collect_phone: "N",
        custom_conv: "Y",
      });
      dispatch(updatePaymentPermission(paymentPermission));
      dispatch(updateLogoAndFavicon(companyLogoDetails));
      dispatch(
        updateCompanyDetails({ ...companyDetails, ...companyLogoDetails })
      );
      setPaymentLogo(companyLogoDetails?.logo);
      productLinkData.setProductData([]);
      productLinkData.setTitle(
       "Make a Payment"
      );

      // Returning from a redirect payment (Cash App, bank, PayPal) restores the
      // amount the customer entered.
      const returnedAmount = searchParams.get("amount");
      if (isValidQuickCheckoutAmount(returnedAmount)) {
        setAmount(returnedAmount);
        productLinkData.setEditVal(Number(returnedAmount));
      }
    } catch (error) {
      console.log("quick checkout details error =====", error);
      goToInvalid(navigate, "unavailable");
    } finally {
      productLinkData.setIsLoading(false);
    }
  };

  const getProductDetails = async () => {
    await getQuickCheckoutDetails();
  };

  useEffect(() => {
    getProductDetails();
  }, []);

  const onOldCardDataGet = async () => {
    try {
      let productSlug = linkSlugParts;
      let payload = {
        accountid: userData?.data?.accountData?.accountid,
        accounts_id: userData?.data?.accountData?.id,
        clusterid: productSlug[1],
      };
      await dispatch(oldPaymentMethodsGet(payload)).unwrap();
    } catch (error: any) {
      console.log("old card data get erro:-->", error?.message);
    }
  };

  // A login flag left in browser storage without its token belongs to an
  // earlier visitor (shared device, expired session). Requests then go out
  // as a guest, so the page must not show or use that person's details.
  // A login for another cluster cannot pay this cluster's checkout (the API
  // treats such a caller as a guest): the page does the same, as a payment
  // link does, instead of sending that account's ids. A stale login (no
  // token left) is dropped the same way.
  const loginClusterId = userData?.data?.accountData?.clusterid;
  const isOtherClusterLogin =
    isLogin &&
    Boolean(loginClusterId) &&
    Boolean(linkClusterId) &&
    String(loginClusterId) !== String(linkClusterId);
  const hasStaleLogin =
    (isLogin && !localStorage.getItem(TOKEN_STORAGE_KEY)) ||
    isOtherClusterLogin;
  useEffect(() => {
    // A "session expired" flag left by an earlier page would pop up later;
    // this public page has no session to expire.
    dispatch(updateTokenExpiredStatus(false));
    // The "payment in progress" flag is stored with the app; leaving this
    // page must not leave it set for the agreement / invoice pages.
    return () => {
      dispatch(setIsPaymentProcessing(false));
    };
  }, [dispatch]);
  useEffect(() => {
    if (!hasStaleLogin) return;
    dispatch(onSetIsLogin(false));
    dispatch(clearAuthSlice());
    dispatch(clearPaymentMethodSlice());
    paymentProcess.setEmail("");
  }, [hasStaleLogin]);

  useEffect(() => {
    if (isLogin && !hasStaleLogin) {
      onOldCardDataGet();
    }
  }, [isLogin]);

  const scrollToStepper = () => {
    if (stepperAnchorRef.current) {
      stepperAnchorRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      return;
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    if (showFinalDisclosure) {
      requestAnimationFrame(() => {
        scrollToStepper();
      });
    }
  }, [showFinalDisclosure]);

  const usableSavedPaymentMethods = useDefaultPaymentMethodView({
    cardList,
    bankList,
    walletList,
    paymentMethodList,
    paymentPermission,
    isLogin,
    hasLoadedPaymentMethods,
    setShowSavedMethods: setIsAddNewCard,
    setDataPayment,
    selectedMethod,
    setSelectedMethod,
  });

  useEffect(() => {
    setPaymentProcessIsAddNewCard(
      isAddNewCard && usableSavedPaymentMethods.hasUsableSavedMethods
    );
  }, [
    isAddNewCard,
    setPaymentProcessIsAddNewCard,
    usableSavedPaymentMethods.hasUsableSavedMethods,
  ]);

  // payment permission
  useEffect(() => {
    if (productLinkData.productLinkData) {
      const { paymentPermission } = productLinkData.productLinkData || {};
      if (paymentPermission) {
        let methodList = [];

        // Stax PayPal is verified through the product-link-only Stax flow,
        // so quick checkout offers PayPal only on other gateways.
        const isPayPalSupported =
          paymentPermission?.pp_gateway_name?.name !== PaymentGateway.STAX;

        if (paymentPermission.cc_ext_availability === SettingsValue.YES) {
          methodList.push({
            name: "Card",
            icon: <CardIcon />,
          });
        }

        if (paymentPermission.ach_ext_availability === SettingsValue.YES) {
          methodList.push({
            name: "Bank",
            icon: <BankIconSm />,
          });
        }

        if (paymentPermission.capp_ext_availability === SettingsValue.YES) {
          methodList.push({
            name: "Cash App Pay",
            icon: <CashAppIcon />,
          });
        }
        // Wire is not offered: it creates its order through a separate flow,
        // not dc_orders via the quick checkout API.
        if (paymentPermission.amzp_ext_availability === SettingsValue.YES) {
          methodList.push({
            name: "Amazon Pay",
            icon: <AmazonPay />,
          });
        }
        if (
          paymentPermission.pp_ext_availability === SettingsValue.YES &&
          isPayPalSupported
        ) {
          methodList.push({
            name: "PayPal",
            icon: <PaypalIcon />,
          });
        }
        if (paymentPermission.cc_ext_availability === SettingsValue.NO) {
          setSelectedMethod(methodList?.[0]?.name);
        }

        setPaymentMethodList(methodList);
      } else {
        setPaymentMethodList(defaultPaymentMethodList);
      }
    }
  }, [productLinkData.productLinkData]);

  // Product link checks subscription limits on email blur; quick checkout is
  // one-time only, so there is nothing to check.
  const handleEmailBlur = () => {};

  //bank name get
  const findBankByRoutingNumber = async (routingNumber: string) => {
    setIsRingLoading(true);
    try {
      let bankObj = await getBankName(routingNumber);

      if (bankObj?.type === "success") {
        bankState.setBankName(bankObj?.bank_name);
        bankState.setBank(bankObj?.bank_name);
      } else {
        paymentErrors.setErrMsg("Invalid routing number");
        setIsRingLoading(false);
      }
    } catch (error) {
      console.log("error =====", error);
      paymentErrors.setErrMsg("Invalid routing number");
      setIsRingLoading(false);
    } finally {
      setIsRingLoading(false);
    }
  };

  // card number
  const handleChangeCard = (e: any, name: string) => {
    let value = { ...cardState.values };
    if (name === "x_card_num") {
      let icon = getCardType(e.target.value) || cardState.iconType;
      if (icon === "amex") {
        if (e.target.value.length < 18) {
          const cardNumber = e.target.value.replace(/\s/g, "");
          const formattedCardNumber = cardNumber
            .replace(/\D/g, "")
            .replace(/(\d{4})(\d{6})(\d{5})/, "$1 $2 $3");
          cardState.setCardNumber(formattedCardNumber);
          value = { ...cardState.values, [name]: e.target.value };
        }
      } else {
        if (e.target.value.length < 20) {
          const cardNumber = e.target.value.replace(/\s/g, "");
          const formattedCardNumber = cardNumber
            .replace(/\D/g, "")
            .replace(/(\d{4})(?=\d)/g, "$1 ");
          cardState.setCardNumber(formattedCardNumber);
          value = { ...cardState.values, [name]: e.target.value };
        }
      }
    } else if (name === "x_card_code") {
      if (cardState.iconType === "amex") {
        if (e.target.value.length < 5) {
          value = { ...cardState.values, [name]: e.target.value };
        }
      } else {
        if (e.target.value.length < 4) {
          value = { ...cardState.values, [name]: e.target.value };
        }
      }
    } else if (name === "x_exp_date") {
      const inputValue = e.target.value;
      cardState.setIsDateErr(false);
      if (inputValue?.length < 6) {
        const isValidDate: any = expiryDateValidation(inputValue);
        const formattedDate = formatExpirationDate(inputValue);
        value = { ...value, [name]: formattedDate };
        !isValidDate?.isValid &&
          inputValue?.length === 5 &&
          cardState.setIsDateErr(true);
      }
    } else if (name === "x_country") {
      value = { ...value, [name]: e?.value };
    } else if (name === "x_state") {
      value = { ...value, [name]: e?.name };
    } else {
      value = { ...value, [name]: e.target.value };
    }

    cardState.setValues(value);
  };

  //change bank
  const handleChangeBank = (e: any, name: string) => {
    if (name === "routing_number") {
      if (e?.target?.value?.length < 10) {
        cardState.setValues({ ...cardState.values, [name]: e.target.value });
        bankState.setBankName("");
        bankState.setBank("");
        paymentErrors.setErrMsg(undefined);
        if (e?.target?.value?.length === 9) {
          findBankByRoutingNumber(e?.target?.value);
        }
      }
    } else if (name === "r_account_number") {
      bankState.setnumberNotmatchedError(false);
      cardState.setValues({ ...cardState.values, [name]: e?.target.value });
      if (
        cardState.values?.account_number?.length === e?.target?.value?.length
      ) {
        bankState.setnumberNotmatchedError(
          cardState.values?.account_number !== e?.target?.value
        );
      }
    } else {
      cardState.setValues({ ...cardState.values, [name]: e?.target?.value });
    }
  };

  //card icon
  useEffect(() => {
    if (!cardState.cardNumber) {
      cardState.setIconType("");
      cardState.setValues({ ...cardState.values, card_type: "" });
      return;
    } else {
      cardState.setIconType(getCardType(cardState.cardNumber));
      cardState.setValues({
        ...cardState.values,
        card_type: getCardType(cardState.cardNumber),
      });
    }
  }, [cardState.cardNumber]);

  // button disable
  useEffect(() => {
    if (isAddNewCard) {
      let response = checkOldPaymentDetails({ dataPayment });
      let checkEmail =
        savedCardsEmail &&
        savedCardsEmail?.trim() === "" &&
        validateEmail(savedCardsEmail);
      const isPhone =
        productLinkData.prodPermission.collect_phone === "Y"
          ? !checkPhone(productLinkData?.phoneNumber)
          : false;

      setIsDisabled(response || checkEmail || isPhone);
    } else {
      let response = checkNewPaymentDetails({
        values: cardState.values,
        cc_billing_address: portalSettings?.cc_billing_address,
        cc_billing_addr_mandatory: portalSettings?.cc_billing_addr_mandatory,
        selectedMethod,
        email: paymentProcess.email,
        isErr: cardState.isErr,
        errMsg: paymentErrors.errMsg,
        collectAddress: productLinkData.prodPermission.collect_address === "Y",
      });

      const isPhone =
        productLinkData.prodPermission.collect_phone === "Y"
          ? !checkPhone(productLinkData?.phoneNumber)
          : false;

      setIsDisabled(response || isPhone);
    }
  }, [
    selectedMethod,
    cardState.values,
    paymentProcess.email,
    isAddNewCard,
    dataPayment,
    cardState.isErr,
    paymentErrors.errMsg,
    productLinkData.prodPermission,
    productLinkData?.phoneNumber,
  ]);

  //back to start on failed payment
  const handleBackToStart = () => {
    renewAttemptId();
    cardState.resetCardState();
    paymentErrors.clearErrors();
    // A logged-in customer's email field is locked to their own address;
    // clearing it would leave every Pay button disabled.
    paymentProcess.setEmail(
      isLogin ? userData?.data?.accountData?.email || "" : ""
    );
    paymentProcess.setPaymentProcess(null);
    bankState.resetBankState();
    setInitialDisclosureAccept(false);
    // Keep the company's logo (the default is only for a company without one).
    setPaymentLogo(companyDetails?.logo || logo);
    // A failed Google Pay leaves no method panel open; go back to the first.
    if (selectedMethod === "Google Pay") {
      setSelectedMethod(paymentMethodList?.[0]?.name || "Card");
    }
    dispatch(onSetFinalDisclosureLoading(false));
    dispatch(onSetShowDisclosure(false));
  };

  //wire paymnet
  const paymentViaWire = async () => {
    try {
      let productSlug = linkSlugParts;
      let payload = {
        payment_gateway_id: "",
        amount: parseFloat(amount.toString()).toFixed(2),
        clusterid: productSlug[1],
        x_method: "WTO",
        lead_id: accountLeadData?.lead_id,
        email: userData?.data?.accountData?.email ?? paymentProcess.email,
        accountid: userData?.data?.accountData?.accountid,
        description: "Online Payment",
        payment_note: !isRecurring ? "One-time" : "Subscription",
        isProductLink: true,
        ivr_type: "CUST_PRTL",
      };
      let res: any = await dispatch(wirePaymentProcess(payload)).unwrap();
      if (res.body.success) {
        const { data } = res?.body;
        bankState.setWireDetails({
          email: data?.email || "",
          bank: data?.bank_name || "",
          routing: data?.routing_number || "",
          account: data?.account_number || "",
          swift: data?.swift_code || "",
          dueDate:
            moment(
              moment().add(res?.body?.data?.wire_deadline || 0, "days")
            ).format("MM/DD/YYYY") || "",
        });
        paymentProcess.setInvoiceNumber(data?.invoice_number || "");
        paymentProcess.setInvoiceSlug(data?.invoice_slug);
        paymentProcess.setOrderId(data?.invoice_id);
        paymentProcess.setEmail(data?.email);
        paymentProcess.setIsWirePayment(true);
        paymentProcess.setPaymentProcess("true");
        paymentProcess.setAccountId(
          data?.accountid || userData?.data?.accountData?.accountid
        );
        await handleSendEmail({
          email: data?.email,
          invoice_slug: data?.invoice_slug,
          order_id: data?.invoice_id,
          isDuplicate: data?.isDuplicate,
          accountId: data?.accountid || userData?.data?.accountData?.accountid,
        });
      } else {
        paymentErrors.setFailedReason(res?.body?.data?.msg);
        paymentProcess.setPaymentProcess("false");
      }
    } catch (error) {
      console.log(" paymentViaWire error=====", error);
    }
  };

  const createPayloadCardBank = async (value: any) => {
    const { lead_owner, balance, first_name, last_name, accountid } =
      userData?.data?.accountData || {};
    const {
      x_payment_method,
      x_city,
      x_address,
      address2,
      x_zip,
      x_state,
      x_country,
      email: emailVal,
      iconType: cardType,
      x_card_num,
      x_card_code,
      x_exp_date,
      choldername,
      routing_number,
      account_number,
      bankName,
      bank,
      cc_token,
      cc_token2,
      stripe_token,
      cppid,
      pc_atid,
      use_old_card,
    } = value;

    let payload = {
      lead_id: accountLeadData?.lead_id,
      cluster_id: linkClusterId,
      balance: balance,
      x_payment_method: x_payment_method,
      x_email: emailVal,
      city: x_city,
      address1: x_address,
      address2: address2,
      zipcode: x_zip,
      // A new card sends the state code the fee was priced with; a new
      // account is created with it, and the charge prices from the account.
      state:
        x_payment_method === "CC" && use_old_card !== "1" && cardFeeState
          ? cardFeeState
          : x_state,
      country: x_country,
      first_name: first_name,
      last_name: last_name,
      accountid: accountid,
      amount_type: "amount",
      amount: amount,
      use_old_card: use_old_card,
      description: "Online Payment",
      payment_note: !isRecurring ? "One-time" : "Subscription",
      customer_device: getOperatingSystem(),
      card_type: cardType ? cardType : cardState.iconType,
      old_cardnumber: x_card_num ? x_card_num?.replaceAll(" ", "") : "",
      old_cvv: x_card_code ? x_card_code : "",
      old_expiry: x_exp_date ? x_exp_date : "",
      old_4digit: x_card_num ? x_card_num?.slice(-4) : "",
      old_type: cardState.iconType ? cardState.iconType : "",
      old_zip: x_zip ? x_zip : "",
      cvv: x_card_code ? x_card_code : "",
      cexpiry: x_exp_date ? x_exp_date : "",
      ccardnumber: x_card_num ? x_card_num?.replaceAll(" ", "") : "",
      choldername: choldername ? choldername : "",
      aholdername: choldername ? choldername : "",
      routing_number: routing_number ? routing_number : "",
      account_number: account_number ? account_number : "",
      bank_name: bankName ? bankName : "",
      bank_address: bank ? bank : "",
      cc_token: cc_token ? cc_token : "",
      cc_token2: cc_token2 || cc_token || "",
      stripe_token: stripe_token ? stripe_token : "",
      agent: lead_owner,
      lead_owner: lead_owner,
      cppid: cppid ?? 0,
      pc_atid: pc_atid ?? 0,
      custom_conv: QUICK_CHECKOUT_CUSTOM_CONV,
    };
    return payload;
  };

  const handleCardBankPayment = async (values: any) => {
    try {
      let payload = await createPayloadCardBank(values);
      let res: any = await submitCheckout(payload);
      if (res?.body?.success) {
        let { data } = res?.body;
        paymentProcess.setInvoiceNumber(data?.invoice_number || "");
        paymentProcess.setPaymentProcess("true");
        paymentProcess.setInvoiceSlug(data?.invoice_slug);
        paymentProcess.setOrderId(data?.invoice_id);
        paymentProcess.setAmountDisplay(data?.total_amount);
        paymentProcess.setEmail(data?.email);
        paymentProcess.setLeadId(data?.lead_id);
        paymentProcess.setAccountId(
          data?.accountid || userData?.data?.accountData?.accountid
        );
        await handleSendEmail({
          email: data?.email,
          invoice_slug: data?.invoice_slug,
          order_id: data?.invoice_id,
          isDuplicate: data?.isDuplicate,
          accountId: data?.accountid || userData?.data?.accountData?.accountid,
        });
      } else if (isQuickCheckoutPendingReply(res)) {
        await showPaymentPending(res?.body?.data, payload.x_email);
      } else {
        paymentProcess.setPaymentProcess("false");
        paymentErrors.setFailedReason(quickCheckoutFailureReason(res));
        await sendFailedPaymentEmail(res?.body?.data, payload.x_email);
      }
      setIsBTNLoading(false);
    } catch (error) {
      paymentProcess.setPaymentProcess("false");
      paymentErrors.setFailedReason("Payment failed, server error");
      setIsBTNLoading(false);
      console.log("handleCardBankPayment error =====", error);
    } finally {
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
    }
  };

  const handleSubmit = async () => {
    try {
      setIsBTNLoading(true);
      if (selectedMethod === "Wire") {
        paymentViaWire();
      } else {
        if (!isAddNewCard) {
          let payload = {
            x_payment_method: getMethodCode(selectedMethod),
            email: paymentProcess.email,
            x_card_num: cardState.values?.x_card_num?.replaceAll(" ", ""),
            x_card_code: cardState.values?.x_card_code,
            x_exp_date: cardState.values?.x_exp_date,
            iconType: cardState.iconType,
            routing_number: cardState.values?.routing_number,
            account_number: cardState.values?.account_number,
            cc_token: "",
            cc_token2: "",
            stripe_token: "",
            bankName: bankState.bankName,
            bank: bankState.bank,
            x_city:
              cardState.values?.x_city ||
              userData?.data?.accountData?.city ||
              "",
            x_address:
              cardState.values?.x_address ||
              userData?.data?.accountData?.address1 ||
              "",
            address2:
              cardState.values?.address2 ||
              userData?.data?.accountData?.address2 ||
              "",
            x_zip:
              cardState.values?.x_zip ||
              userData?.data?.accountData?.postal_code ||
              "",
            x_state:
              cardState.values?.x_state ||
              userData?.data?.accountData?.state ||
              "",
            x_country:
              cardState.values?.x_country ||
              userData?.data?.accountData?.country ||
              "",
            choldername: cardState.values?.choldername,
            cppid: "",
            use_old_card: "0",
          };
          await handleCardBankPayment(payload);
        } else {
          let method = "CC";
          if (
            dataPayment?.selectedBank &&
            Object.keys(dataPayment?.selectedBank).length > 0
          ) {
            method = "ACH";
          }
          let payload = {
            x_payment_method: method,
            email: savedCardsEmail,
            x_card_num:
              method === "CC"
                ? dataPayment?.selectedCard?.cc_4digit
                : dataPayment?.selectedBank?.cc_4digit,
            x_card_code:
              method === "CC"
                ? dataPayment?.selectedCard?.cc_cvv
                : dataPayment?.selectedBank?.cc_cvv,
            x_exp_date:
              method === "CC"
                ? dataPayment?.selectedCard?.cc_expiry
                : dataPayment?.selectedBank?.cc_expiry,
            iconType:
              method === "CC"
                ? dataPayment?.selectedCard?.cc_type
                : dataPayment?.selectedBank?.cc_type,
            routing_number:
              method === "CC"
                ? dataPayment?.selectedCard?.routing_number
                : dataPayment?.selectedBank?.routing_number,
            account_number:
              method === "CC"
                ? dataPayment?.selectedCard?.account_number
                : dataPayment?.selectedBank?.account_number,
            cc_token:
              method === "CC"
                ? dataPayment?.selectedCard?.cc_token
                : dataPayment?.selectedBank?.cc_token,
            cc_token2:
              method === "CC"
                ? dataPayment?.selectedCard?.cc_token2
                : dataPayment?.selectedBank?.cc_token2,
            stripe_token:
              method === "CC"
                ? dataPayment?.selectedCard?.cc_token
                : dataPayment?.selectedBank?.cc_token,
            bankName:
              method === "CC"
                ? dataPayment?.selectedCard?.bank_name
                : dataPayment?.selectedBank?.bank_name,
            x_city:
              method === "CC"
                ? dataPayment?.selectedCard?.billing_city
                : dataPayment?.selectedBank?.billing_city,
            x_address:
              method === "CC"
                ? dataPayment?.selectedCard?.billing_address
                : dataPayment?.selectedBank?.billing_address,
            address2:
              method === "CC"
                ? dataPayment?.selectedCard?.billing_address2
                : dataPayment?.selectedBank?.billing_address2,
            x_zip:
              method === "CC"
                ? dataPayment?.selectedCard?.billing_zip
                : dataPayment?.selectedBank?.billing_zip,
            x_state:
              method === "CC"
                ? dataPayment?.selectedCard?.billing_state
                : dataPayment?.selectedBank?.billing_state,
            x_country:
              method === "CC"
                ? dataPayment?.selectedCard?.billing_country
                : dataPayment?.selectedBank?.billing_country,
            choldername:
              method === "CC"
                ? dataPayment?.selectedCard?.account_name
                : dataPayment?.selectedBank?.account_name,
            cppid:
              method === "CC"
                ? dataPayment?.selectedCard?.id
                : dataPayment?.selectedBank?.id,
            use_old_card: "1",
          };
          await handleCardBankPayment(payload);
        }
      }
    } catch (error) {
      console.log("handleSubmit error =====", error);
    } finally {
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
      return true;
    }
  };

  const handleGooglePay = async (value: any, total_amount: any) => {
    if (!ensureAmountEntered()) return;
    try {
      setIsGoogleLoading(true);
      const tokenData = JSON.parse(value?.tokenizationData?.token);
      const { lead_owner, balance, first_name, last_name, accountid } =
        userData?.data?.accountData || {};
      let payload = {
        lead_id: accountLeadData?.lead_id,
        cluster_id: linkClusterId,
        balance: balance,
        x_payment_method: "GP",
        x_email: paymentProcess.email,
        city: tokenData?.card?.address_city || "",
        address1: tokenData?.card?.address_line1 || "",
        address2: tokenData?.card?.address_line2 || "",
        zipcode: tokenData?.card?.address_zip || "",
        state: tokenData?.card?.address_state || "",
        country: tokenData?.card?.address_country || "",
        first_name: first_name,
        last_name: last_name,
        accountid: accountid,
        amount_type: "amount",
        amount: amount,
        use_old_card: !isAddNewCard ? "0" : "1",
        description: "Online Payment",
        payment_note: !isRecurring ? "One-time" : "Subscription",
        customer_device: getOperatingSystem(),
        card_type: tokenData?.card?.brand,
        old_cardnumber: tokenData?.card?.last4,
        old_cvv: "",
        old_expiry: `${tokenData?.card?.exp_month}/${tokenData?.card?.exp_year}`,
        old_4digit: tokenData?.card?.last4 || "",
        old_type: tokenData?.card?.brand || "",
        old_zip: tokenData?.card?.address_zip || "",
        cvv: "",
        cexpiry: `${tokenData?.card?.exp_month}/${tokenData?.card?.exp_year}`,
        ccardnumber: tokenData?.card?.last4 || "",
        choldername: "",
        aholdername: "",
        routing_number: "",
        account_number: "",
        bank_name: "",
        bank_address: "",
        cc_token: tokenData?.id,
        stripe_token: tokenData?.id,
        agent: lead_owner,
        lead_owner: lead_owner,
        custom_conv: QUICK_CHECKOUT_CUSTOM_CONV,
      };
      let res: any = await submitCheckout(payload);
      if (res?.body?.success) {
        let { data } = res?.body;
        paymentProcess.setInvoiceNumber(data?.invoice_number || "");
        paymentProcess.setPaymentProcess("true");
        paymentProcess.setInvoiceSlug(data?.invoice_slug);
        paymentProcess.setOrderId(data?.invoice_id);
        paymentProcess.setAmountDisplay(data?.total_amount);
        paymentProcess.setEmail(data?.email);
        paymentProcess.setLeadId(data?.lead_id);
        paymentProcess.setAccountId(
          data?.accountid || userData?.data?.accountData?.accountid
        );
        await handleSendEmail({
          email: data?.email,
          invoice_slug: data?.invoice_slug,
          order_id: data?.invoice_id,
          isDuplicate: data?.isDuplicate,
          accountId: data?.accountid || userData?.data?.accountData?.accountid,
        });
      } else if (isQuickCheckoutPendingReply(res)) {
        await showPaymentPending(res?.body?.data, payload.x_email);
      } else {
        paymentProcess.setPaymentProcess("false");
        paymentErrors.setFailedReason(quickCheckoutFailureReason(res));
        await sendFailedPaymentEmail(res?.body?.data, payload.x_email);
      }
      setIsGoogleLoading(false);
    } catch (error) {
      paymentProcess.setPaymentProcess("false");
      paymentErrors.setFailedReason("Payment failed, server error");
      setIsGoogleLoading(false);
      console.log("hanleCardBankPayment error =====", error);
    } finally {
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
    }
  };
  // One PayPal setup at a time (a double tap would start two).
  const payPalStartingRef = useRef(false);
  const createPayPalIntent = async () => {
    if (!ensureAmountEntered()) return;
    if (payPalStartingRef.current) return;
    payPalStartingRef.current = true;
    try {
      const payload = {
        is_recurring: "Y",
        phone_number: userData?.data?.accountData?.phone,
        first_name: userData?.data?.accountData?.first_name,
        last_name: userData?.data?.accountData?.last_name,
        accountid: userData?.data?.accountData?.accountid,
        amount: amount,
        agent: userData?.data?.accountData?.lead_owner,
        lead_owner: userData?.data?.accountData?.lead_owner,
        lead_id: accountLeadData?.lead_id,
        clusterid: linkClusterId,
        success_url: buildQuickCheckoutReturnUrl({
          amount,
          method: "paypal",
          status: "success",
        }),
        cancel_url: buildQuickCheckoutReturnUrl({
          amount,
          method: "paypal",
          status: "cancel",
        }),
        start_date: new Date(),
        isProductLink: true,
        email: paymentProcess.email,
      };
      let res: any = await dispatch(createPaypalToken(payload));
      if (res?.payload?.body?.success) {
        saveQuickCheckoutPayPalOrder({
          url: res?.payload?.body?.result?.url,
          token: res?.payload?.body?.result?.token,
          xEmail: paymentProcess.email,
          amount: amount,
        });

        if (
          portalSettings?.payment_agreement_opt === SettingsValue.CUSTOM ||
          portalSettings?.payment_agreement_opt === SettingsValue.DEFAULT
        ) {
          dispatch(onSetShowDisclosure(true));
        } else {
          window.location.href = res?.payload?.body?.result?.url;
        }
      } else {
        paymentErrors.setFailedReason(
          res?.payload?.body?.data?.msg ||
            "PayPal is not available right now. Please try again."
        );
      }
    } catch (error) {
      console.log("createPayPalIntent error =====", error);
    } finally {
      payPalStartingRef.current = false;
    }
  };

  const handleSendEmail = async ({
    email,
    invoice_slug,
    order_id,
    accountId,
    isDuplicate,
  }: any) => {
    // The API returns isDuplicate when this payment was already recorded,
    // e.g. by the Cash App QR tab; that request sent the receipt.
    if (isDuplicate) return;
    try {
      let value = {
        invoice_slug: invoice_slug,
        email: email,
        order_id,
        accountId,
      };
      await sendEmail(value);
    } catch (error) {
      console.error("Failed to send email:", error);
    }
  };

  // The API returns the order it recorded for a failed payment; the customer
  // gets the failed-payment email for that order.
  const sendFailedPaymentEmail = async (
    data: any,
    typedEmail?: string | null
  ) => {
    if (!data?.invoice_id) return;
    await handleSendEmail({
      email: data?.email || typedEmail || userData?.data?.accountData?.email,
      order_id: data?.invoice_id,
      accountId: data?.accountid || userData?.data?.accountData?.accountid,
      // The same declined intent posted again: its email already went out.
      isDuplicate: data?.isDuplicate,
    });
  };

  const verifyCashAppBankPayment = async (
    method: any,
    email: any,
    amount: any
  ) => {
    try {
      const custom_conv = QUICK_CHECKOUT_CUSTOM_CONV;
      const { lead_owner, balance, first_name, last_name, accountid } =
        userData?.data?.accountData || {};
      let payload = {
        lead_id: accountLeadData?.lead_id,
        cluster_id: linkClusterId,
        balance: balance,
        x_payment_method: method,
        x_email: email,
        city: "",
        address1: "",
        address2: "",
        zipcode: "",
        state: "",
        country: "",
        first_name: first_name,
        last_name: last_name,
        accountid: accountid,
        amount_type: "amount",
        amount: amount,
        use_old_card: !isAddNewCard ? "0" : "1",
        description: "Online Payment",
        payment_note: !isRecurring ? "One-time" : "Subscription",
        customer_device: getOperatingSystem(),
        card_type: "",
        old_cardnumber: "",
        old_cvv: "",
        old_expiry: "",
        old_4digit: "",
        old_type: "",
        old_zip: "",
        cvv: "",
        cexpiry: "",
        ccardnumber: "",
        choldername: "",
        aholdername: "",
        routing_number: "",
        account_number: "",
        bank_name: "",
        bank_address: "",
        cc_token: "",
        stripe_token: "",
        agent: lead_owner,
        lead_owner: lead_owner,
        transaction_id: searchParams.has("payment_intent")
          ? searchParams.get("payment_intent")
          : searchParams.get("setup_intent"),
        is3ds: method === "CC" ? true : false,
        custom_conv,
      };
      let res: any = await submitCheckout(payload);
      if (res?.body?.success) {
        let { data } = res?.body;
        paymentProcess.setInvoiceNumber(data?.invoice_number || "");
        paymentProcess.setPaymentProcess("true");
        paymentProcess.setInvoiceSlug(data?.invoice_slug);
        paymentProcess.setOrderId(data?.invoice_id);
        paymentProcess.setAmountDisplay(data?.total_amount);
        paymentProcess.setEmail(data?.email);
        paymentProcess.setLeadId(data?.lead_id);
        paymentProcess.setIsScreenLoading(false);
        setIsScreenLoading(false);
        paymentProcess.setAccountId(
          data?.accountid || userData?.data?.accountData?.accountid
        );
        await handleSendEmail({
          email: data?.email,
          invoice_slug: data?.invoice_slug,
          order_id: data?.invoice_id,
          isDuplicate: data?.isDuplicate,
          accountId: data?.accountid || userData?.data?.accountData?.accountid,
        });
      } else if (isQuickCheckoutPendingReply(res)) {
        paymentProcess.setIsScreenLoading(false);
        setIsScreenLoading(false);
        await showPaymentPending(res?.body?.data, email);
      } else {
        paymentProcess.setPaymentProcess("false");
        paymentErrors.setFailedReason(quickCheckoutFailureReason(res));
        paymentProcess.setIsScreenLoading(false);
        setIsScreenLoading(false);
        await sendFailedPaymentEmail(res?.body?.data, email);
      }
    } catch (error) {
      paymentProcess.setPaymentProcess("false");
      paymentErrors.setFailedReason("Payment failed");
      paymentProcess.setIsScreenLoading(false);
      setIsScreenLoading(false);
      console.log("error =====", error);
    } finally {
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
      paymentProcess.setIsScreenLoading(false);
      setIsScreenLoading(false);
    }
  };

  const verifyStripeCardPayment = async ({
    intent,
    email,
    error,
    principalAmount,
    failedIntent,
  }: VerifyStripeCardPaymentParams) => {
    // A declined card is posted as well: the API reads the intent from Stripe
    // and records the failed order. Without an intent there is nothing to
    // post.
    const declineMessage = error?.message;
    if (error && !failedIntent) {
      paymentProcess.setPaymentProcess("false");
      paymentErrors.setFailedReason(error.message);
      paymentProcess.setIsScreenLoading(false);
      setIsScreenLoading(false);
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
      await dispatch(onSetFinalDisclosureLoading(false));
      await dispatch(onSetShowDisclosure(false));
    } else {
      try {
        const { lead_owner, balance, first_name, last_name, accountid } =
          userData?.data?.accountData || {};
        let payload = {
          lead_id: accountLeadData?.lead_id,
          cluster_id: linkClusterId,
          balance: balance,
          x_payment_method: "CC",
          x_email: email || userData?.data?.accountData?.email,
          city: "",
          address1: "",
          address2: "",
          zipcode: "",
          state: "",
          country: "",
          first_name: first_name,
          last_name: last_name,
          accountid: accountid,
          amount_type: "amount",
          amount: principalAmount ?? amount,
          use_old_card: !isAddNewCard ? "0" : "1",
          description: "Online Payment",
          payment_note: !isRecurring ? "One-time" : "Subscription",
          customer_device: getOperatingSystem(),
          card_type: "",
          old_cardnumber: "",
          old_cvv: "",
          old_expiry: "",
          old_4digit: "",
          old_type: "",
          old_zip: "",
          cvv: "",
          cexpiry: "",
          ccardnumber: "",
          choldername: "",
          aholdername: "",
          routing_number: "",
          account_number: "",
          bank_name: "",
          bank_address: "",
          cc_token: "",
          stripe_token: "",
          agent: lead_owner,
          lead_owner: lead_owner,
          transaction_id: error ? failedIntent : intent,
          is3ds: true,
          custom_conv: QUICK_CHECKOUT_CUSTOM_CONV,
        };
        let res: any = await submitCheckout(payload);
        if (res?.body?.success) {
          let { data } = res?.body;
          paymentProcess.setInvoiceNumber(data?.invoice_number || "");
          paymentProcess.setPaymentProcess("true");
          paymentProcess.setInvoiceSlug(data?.invoice_slug);
          paymentProcess.setOrderId(data?.invoice_id);
          paymentProcess.setAmountDisplay(data?.total_amount);
          paymentProcess.setEmail(data?.email);
          paymentProcess.setLeadId(data?.lead_id);
          paymentProcess.setIsScreenLoading(false);
          setIsScreenLoading(false);
          paymentProcess.setAccountId(
            data?.accountid || userData?.data?.accountData?.accountid
          );
          await handleSendEmail({
            email: data?.email,
            invoice_slug: data?.invoice_slug,
            order_id: data?.invoice_id,
            isDuplicate: data?.isDuplicate,
            accountId:
              data?.accountid || userData?.data?.accountData?.accountid,
          });
        } else if (isQuickCheckoutPendingReply(res)) {
          paymentProcess.setIsScreenLoading(false);
          setIsScreenLoading(false);
          await showPaymentPending(res?.body?.data, email);
        } else {
          paymentProcess.setPaymentProcess("false");
          paymentErrors.setFailedReason(
            declineMessage || quickCheckoutFailureReason(res)
          );
          paymentProcess.setIsScreenLoading(false);
          setIsScreenLoading(false);
          await sendFailedPaymentEmail(res?.body?.data, email);
        }
      } catch (error) {
        paymentProcess.setPaymentProcess("false");
        paymentErrors.setFailedReason(declineMessage || "Payment failed");
        paymentProcess.setIsScreenLoading(false);
        setIsScreenLoading(false);
        console.log("error =====", error);
      } finally {
        dispatch(resetDuplicateOrder());
        dispatch(resetCheckDuplicatePayment());
        await dispatch(onSetFinalDisclosureLoading(false));
        await dispatch(onSetShowDisclosure(false));
      }
    }
  };

  const verifyStaxCardBank = async ({
    cc_token,
    cc_token2,
    x_method,
  }: VerifyStaxCardPaymentParams) => {
    dispatch(setIsPaymentProcessing(true));

    try {
      const { lead_owner, balance, first_name, last_name, accountid } =
        userData?.data?.accountData || {};
      let payload = {
        lead_id: accountLeadData?.lead_id,
        cluster_id: linkClusterId,
        balance: balance,
        x_payment_method: x_method,
        x_email: userData?.data?.accountData?.email || paymentProcess.email,
        city: "",
        address1: "",
        address2: "",
        zipcode: "",
        state: "",
        country: "",
        first_name: first_name,
        last_name: last_name,
        accountid: accountid,
        amount_type: "amount",
        amount: amount,
        use_old_card: !isAddNewCard ? "0" : "1",
        description: "Online Payment",
        payment_note: !isRecurring ? "One-time" : "Subscription",
        customer_device: getOperatingSystem(),
        card_type: "",
        old_cardnumber: "",
        old_cvv: "",
        old_expiry: "",
        old_4digit: "",
        old_type: "",
        old_zip: "",
        cvv: "",
        cexpiry: "",
        ccardnumber: "",
        choldername: "",
        aholdername: "",
        routing_number: "",
        account_number: "",
        bank_name: "",
        bank_address: "",
        cc_token: cc_token,
        cc_token2: cc_token2,
        stripe_token: "",
        agent: lead_owner,
        lead_owner: lead_owner,
        is3ds: false,
        custom_conv: QUICK_CHECKOUT_CUSTOM_CONV,
      };

      let res: any = await submitCheckout(payload);
      if (res?.body?.success) {
        let { data } = res?.body;
        paymentProcess.setInvoiceNumber(data?.invoice_number || "");
        paymentProcess.setPaymentProcess("true");
        paymentProcess.setInvoiceSlug(data?.invoice_slug);
        paymentProcess.setOrderId(data?.invoice_id);
        paymentProcess.setAmountDisplay(
          data?.total_amount || data?.amount || payload?.amount
        );
        paymentProcess.setEmail(data?.email);
        paymentProcess.setLeadId(data?.lead_id);
        paymentProcess.setIsScreenLoading(false);
        setIsScreenLoading(false);
        paymentProcess.setAccountId(
          data?.accountid || userData?.data?.accountData?.accountid
        );
        await handleSendEmail({
          email: data?.email,
          invoice_slug: data?.invoice_slug,
          order_id: data?.invoice_id,
          isDuplicate: data?.isDuplicate,
          accountId: data?.accountid || userData?.data?.accountData?.accountid,
        });
      } else if (isQuickCheckoutPendingReply(res)) {
        paymentProcess.setIsScreenLoading(false);
        setIsScreenLoading(false);
        await showPaymentPending(res?.body?.data, payload.x_email);
      } else {
        paymentProcess.setPaymentProcess("false");
        paymentErrors.setFailedReason(quickCheckoutFailureReason(res));
        paymentProcess.setIsScreenLoading(false);
        setIsScreenLoading(false);
        await sendFailedPaymentEmail(res?.body?.data, payload.x_email);
      }
    } catch (error) {
      paymentProcess.setPaymentProcess("false");
      paymentErrors.setFailedReason("Payment failed");
      paymentProcess.setIsScreenLoading(false);
      setIsScreenLoading(false);
      console.log("error =====", error);
    } finally {
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
      await dispatch(onSetFinalDisclosureLoading(false));
      await dispatch(onSetShowDisclosure(false));
    }
  };

  const verifyStaxPayPal = async ({
    transactionId,
    error,
  }: {
    transactionId?: string;
    error?: any;
  }) => {
    setIsStaxPayPalLoading(true);
    let slug = linkSlugParts;
    try {
      const { email, first_name, last_name, accountid, phone } =
        userData?.data?.accountData || {};
      let payload = {
        transaction_id: transactionId,
        lead_id: accountLeadData?.lead_id,
        clusterid: slug[1],
        isrecurring: "N",
        x_method: "PP",
        amount: amount,
        accountid: accountid || "",
        first_name: first_name || "",
        last_name: last_name || "",
        phone_number: phone,
        module: "process_payment",
        isProductLink: true,
        productLinkId: slug[0],
        conv_fee: conFee,
        description: "Online Payment",
        payment_note: "One-time",
        isPayForProduct: true,
        custom_conv: QUICK_CHECKOUT_CUSTOM_CONV,
        email: email,
      };

      let res: any = await dispatch(verifyPaymentsViaStax(payload)).unwrap();
      if (res?.body?.success) {
        let { data } = res?.body;
        paymentProcess.setInvoiceNumber(data?.invoice_number || "");
        paymentProcess.setPaymentProcess("true");
        paymentProcess.setInvoiceSlug(data?.invoice_slug);
        paymentProcess.setOrderId(data?.invoice_id);
        paymentProcess.setAmountDisplay(data?.total_amount || data?.amount);
        paymentProcess.setEmail(data?.email);
        paymentProcess.setLeadId(data?.lead_id);
        paymentProcess.setIsScreenLoading(false);
        setIsScreenLoading(false);
        paymentProcess.setAccountId(
          data?.accountid || userData?.data?.accountData?.accountid
        );
        await handleSendEmail({
          email: data?.email,
          invoice_slug: data?.invoice_slug,
          order_id: data?.invoice_id,
          isDuplicate: data?.isDuplicate,
          accountId: data?.accountid || userData?.data?.accountData?.accountid,
        });
      } else {
        paymentProcess.setPaymentProcess("false");
        paymentErrors.setFailedReason(
          res?.body?.message ??
            res?.body?.msg ??
            res?.body?.data?.gateway_error ??
            res?.body?.data?.message ??
            "Payment failed"
        );
        paymentProcess.setIsScreenLoading(false);
        setIsScreenLoading(false);
        await sendFailedPaymentEmail(res?.body?.data, payload.email);
      }
    } catch (error) {
      paymentProcess.setPaymentProcess("false");
      paymentErrors.setFailedReason("Payment failed");
      paymentProcess.setIsScreenLoading(false);
      setIsScreenLoading(false);
      console.log("error =====", error);
    } finally {
      setIsStaxPayPalLoading(false);
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
      await dispatch(onSetFinalDisclosureLoading(false));
      await dispatch(onSetShowDisclosure(false));
    }
  };

  const sendEmail = async ({
    invoice_slug,
    accountId,
    email,
    shouldSendAll,
    order_id,
    bank_name,
    routing_number,
    account_number,
    swift_code,
  }: any) => {
    try {
      const clusterId = linkClusterId;
      const validEmails = (Array.isArray(email) ? email : [email])
        .map((value: any) => (typeof value === "string" ? value.trim() : ""))
        .filter(Boolean);

      if (invoice_slug) {
        if (!accountId || !clusterId || validEmails.length === 0) {
          console.warn("sendEmail skipped: missing required values", {
            invoice_slug,
            accountid: accountId,
            clusterid: clusterId,
            email,
          });
          return false;
        }

        let payload: any = {
          invoice_slug: invoice_slug,
          accountid: accountId,
          clusterid: clusterId,
          email: validEmails[0],
          shouldSendAll: shouldSendAll,
          // Match the automatic receipt path: recurring product links use an
          // invoice receipt, while one-time product links use the product
          // receipt template.
          email_type: isRecurring
            ? "invoice_receipt"
            : "receipt_on_time_products",
        };

        if (bank_name) {
          payload = {
            ...payload,
            bank_name: bank_name,
            routing_number: routing_number,
            account_number: account_number,
            swift_code: swift_code,
          };
        }

        const response: any = await dispatch(
          invoiceDetailsSend(payload)
        ).unwrap();
        return Boolean(response?.body?.success ?? response?.success);
      } else {
        // A guest is not told the Account ID; the order's receipt token
        // (attached by the API client) lets the email go out without it.
        if (!order_id || validEmails.length === 0) {
          console.warn("sendEmail skipped: missing required values", {
            invoice_id: order_id,
            emails: email,
          });
          return false;
        }

        let payload = {
          invoice_id: order_id,
          ...(accountId ? { accountid: accountId } : {}),
          emails: validEmails,
          shouldSendAll: shouldSendAll,
        };

        const response: any = await dispatch(paymentDownload(payload)).unwrap();
        return Boolean(response?.body?.success ?? response?.success);
      }
    } catch (error) {
      console.log("sendEmail", error);
      return false;
    }
  };

  const handlePayPalPayment = async (tokendata: any, payPalOrder: any) => {
    try {
      const custom_conv = QUICK_CHECKOUT_CUSTOM_CONV;
      let res: any = await dispatch(
        createPaypalPaymentToken(tokendata)
      ).unwrap();

      if (res?.body?.success) {
        const payload1 = {
          cluster_id: linkClusterId,
          x_email: payPalOrder.xEmail,
          x_payment_method: "PP",
          cc_token: res?.body?.data?.cc_token,
          cppid: res?.body?.data?.cppid,
          accountid: userData?.data?.accountData?.accountid,
          lead_id: accountLeadData?.lead_id,
          city: "",
          address1: "",
          address2: "",
          zipcode: "",
          state: "",
          country: "",
          first_name: userData?.data?.accountData?.first_name,
          last_name: userData?.data?.accountData?.last_name,
          amount_type: "amount",
          amount: payPalOrder.amount,
          use_old_card: !isAddNewCard ? "0" : "1",
          description: "Online Payment",
          payment_note: !isRecurring ? "One-time" : "Subscription",
          customer_device: getOperatingSystem(),
          card_type: "",
          old_cardnumber: "",
          old_cvv: "",
          old_expiry: "",
          old_4digit: "",
          old_type: "",
          old_zip: "",
          cvv: "",
          cexpiry: "",
          ccardnumber: "",
          choldername: "",
          aholdername: "",
          routing_number: "",
          account_number: "",
          bank_name: "",
          bank_address: "",
          stripe_token: "",
          agent: userData?.data?.accountData?.lead_owner,
          lead_owner: userData?.data?.accountData?.lead_owner,
          transaction_id: searchParams.has("payment_intent")
            ? searchParams.get("payment_intent")
            : searchParams.get("setup_intent"),
          is3ds: false,
          custom_conv,
        };

        let response: any = await submitCheckout(payload1);

        if (response?.body?.success) {
          let { data } = response?.body;
          paymentProcess.setInvoiceNumber(data?.invoice_number || "");
          paymentProcess.setPaymentProcess("true");
          paymentProcess.setInvoiceSlug(data?.invoice_slug);
          paymentProcess.setOrderId(data?.invoice_id);
          paymentProcess.setAmountDisplay(data?.total_amount);
          paymentProcess.setEmail(data?.email);
          paymentProcess.setLeadId(data?.lead_id);
          paymentProcess.setIsScreenLoading(false);
          setIsScreenLoading(false);
          paymentProcess.setAccountId(
            data?.accountid || userData?.data?.accountData?.accountid
          );
          handleSendEmail({
            email: data?.email,
            invoice_slug: data?.invoice_slug,
            order_id: data?.invoice_id,
            isDuplicate: data?.isDuplicate,
            accountId:
              data?.accountid || userData?.data?.accountData?.accountid,
          });
        } else if (isQuickCheckoutPendingReply(response)) {
          paymentProcess.setIsScreenLoading(false);
          setIsScreenLoading(false);
          await showPaymentPending(response?.body?.data, payload1.x_email);
        } else {
          paymentProcess.setPaymentProcess("false");
          paymentErrors.setFailedReason(quickCheckoutFailureReason(response));
          paymentProcess.setIsScreenLoading(false);
          setIsScreenLoading(false);
          await sendFailedPaymentEmail(response?.body?.data, payload1.x_email);
        }
      } else {
        paymentProcess.setPaymentProcess("false");
        paymentErrors.setFailedReason("Payment failed");
        paymentProcess.setIsScreenLoading(false);
        setIsScreenLoading(false);
      }
    } catch (error) {
      paymentProcess.setPaymentProcess("false");
      paymentErrors.setFailedReason("Payment failed");
      paymentProcess.setIsScreenLoading(false);
      setIsScreenLoading(false);
      console.log("error =====", error);
    } finally {
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
    }
  };

  // paypal cashapp bank
  useEffect(() => {
    //cash app
    if (
      (searchParams.has("payment_intent") ||
        searchParams.has("setup_intent")) &&
      !searchParams.has("checked") &&
      searchParams.has("amount") &&
      searchParams.has("method") &&
      searchParams.get("method") === "cashapp"
    ) {
      setIsScreenLoading(true);
      searchParams.set("checked", "true");
      verifyCashAppBankPayment(
        "CAPP",
        // older return addresses still carry the email
        searchParams.get("email") || takeQuickCheckoutReturnEmail(),
        searchParams.get("amount")
      );
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        navigate({ search: "" });
      }, 500);
    }

    //bank, amazon pay
    if (
      (searchParams.has("payment_intent") ||
        searchParams.has("setup_intent")) &&
      !searchParams.has("checked") &&
      searchParams.has("amount") &&
      (searchParams.get("method") === "bank" ||
        searchParams.get("method") === "amazonPay")
    ) {
      setIsScreenLoading(true);
      searchParams.set("checked", "true");
      verifyCashAppBankPayment(
        searchParams.get("method") === "bank" ? "ACH" : "AMZP",
        searchParams.get("email") || takeQuickCheckoutReturnEmail(),
        searchParams.get("amount")
      );
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        navigate({ search: "" });
      }, 500);
    }
    if (
      (searchParams.has("approval_session_id") ||
        searchParams.has("approval_token_id")) &&
      !searchParams.has("checked") &&
      searchParams.has("status") &&
      searchParams.has("amount") &&
      searchParams.get("method") === "paypal"
    ) {
      searchParams.set("checked", "true");
      // The order saved when the customer left for PayPal (this tab); the
      // persisted one covers a session that started before this change.
      const savedOrder = readQuickCheckoutPayPalOrder() || payPalOrder;
      saveQuickCheckoutPayPalOrder(null);
      if (searchParams.get("status") !== "success") {
        // Cancelled at PayPal: back to the form, nothing was charged.
      } else if (!savedOrder?.token) {
        paymentProcess.setPaymentProcess("false");
        paymentErrors.setFailedReason(
          "We couldn't continue the PayPal payment. Nothing was charged; please try again."
        );
      } else {
        setIsScreenLoading(true);
        handlePayPalPayment({ token: savedOrder.token }, savedOrder);
      }
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        navigate({ search: "" });
      }, 500);
    }
  }, []);

  const handleApplePay = async (
    success: boolean,
    paymentIntent: any,
    failureMessage?: string,
    failedIntentId?: string | null
  ) => {
    // ApplePayButton reports a failure as (false, undefined, message, the
    // declined intent's id). A declined intent is posted, so the API records
    // the failed order; a cancelled sheet has no intent and nothing to post.
    const intentId = success ? paymentIntent?.id : failedIntentId;
    // Still processing at Stripe (ApplePayButton reports it with this
    // message and no intent): it may well go through, so it is neither
    // posted as a decline nor offered for another try.
    if (!intentId && /still processing/i.test(failureMessage || "")) {
      setIsScreenLoading(false);
      paymentProcess.setIsScreenLoading(false);
      await showPaymentPending({ message: failureMessage });
      return;
    }
    if (!intentId) {
      paymentProcess.setPaymentProcess("false");
      paymentErrors.setFailedReason(
        failureMessage || "Apple Pay payment failed. Please try again."
      );
      setIsScreenLoading(false);
      paymentProcess.setIsScreenLoading(false);
      return;
    }
    const { lead_owner, balance, first_name, last_name, accountid } =
      userData?.data?.accountData || {};
    try {
      let payload = {
        lead_id: accountLeadData?.lead_id,
        cluster_id: linkClusterId,
        balance: balance,
        x_payment_method: "AP",
        x_email: paymentProcess.email,
        city: "",
        address1: "",
        address2: "",
        zipcode: "",
        state: "",
        country: "",
        first_name: first_name,
        last_name: last_name,
        accountid: accountid,
        amount_type: "amount",
        amount: amount,
        use_old_card: !isAddNewCard ? "0" : "1",
        description: "Online Payment",
        payment_note: !isRecurring ? "One-time" : "Subscription",
        customer_device: getOperatingSystem(),
        card_type: "",
        old_cardnumber: "",
        old_cvv: "",
        old_expiry: "",
        old_4digit: "",
        old_type: "",
        old_zip: "",
        cvv: "",
        cexpiry: "",
        ccardnumber: "",
        choldername: "",
        aholdername: "",
        routing_number: "",
        account_number: "",
        bank_name: "",
        bank_address: "",
        cc_token: "",
        stripe_token: "",
        agent: lead_owner,
        lead_owner: lead_owner,
        transaction_id: intentId,
        custom_conv: QUICK_CHECKOUT_CUSTOM_CONV,
      };
      let res: any = await submitCheckout(payload);

      if (res?.body?.success) {
        let { data } = res?.body;
        paymentProcess.setInvoiceNumber(data?.invoice_number || "");
        paymentProcess.setPaymentProcess("true");
        paymentProcess.setInvoiceSlug(data?.invoice_slug);
        paymentProcess.setOrderId(data?.invoice_id);
        paymentProcess.setAmountDisplay(data?.total_amount);
        paymentProcess.setEmail(data?.email);
        paymentProcess.setLeadId(data?.lead_id);
        paymentProcess.setIsScreenLoading(false);
        paymentProcess.setAccountId(
          data?.accountid || userData?.data?.accountData?.accountid
        );
        await handleSendEmail({
          email: data?.email,
          invoice_slug: data?.invoice_slug,
          order_id: data?.invoice_id,
          isDuplicate: data?.isDuplicate,
          accountId: data?.accountid || userData?.data?.accountData?.accountid,
        });
      } else if (isQuickCheckoutPendingReply(res)) {
        paymentProcess.setIsScreenLoading(false);
        await showPaymentPending(res?.body?.data, payload.x_email);
      } else {
        paymentProcess.setPaymentProcess("false");
        paymentErrors.setFailedReason(
          failureMessage || quickCheckoutFailureReason(res)
        );
        paymentProcess.setIsScreenLoading(false);
        await sendFailedPaymentEmail(res?.body?.data, payload.x_email);
      }
    } catch (error) {
      paymentProcess.setPaymentProcess("false");
      paymentErrors.setFailedReason(failureMessage || "Payment failed");
      paymentProcess.setIsScreenLoading(false);
      console.log("error =====", error);
    }
  };

  const handleAccept = async () => {
    await dispatch(onSetFinalDisclosureLoading(true));
    try {
      if (isAddNewCard) {
        await handleSubmit();
      } else {
        switch (selectedMethod) {
          case "Card":
            if (
              paymentPermission?.cc_gateway_name?.name ===
                PaymentGateway.STRIPE &&
              cardRef.current
            ) {
              await cardRef.current.callHandleClick();
            } else {
              await handleSubmit();
            }
            break;
          case "Amazon Pay":
            if (amazonRef.current) {
              await amazonRef.current.callHandleClick();
            } else {
              await handleSubmit();
            }
            break;
          case "Bank":
            let check = checkBankManualFormOrPlaid({
              manual_ach: portalSettings?.manual_ach,
              validate_bank_account: portalSettings?.validate_bank_account,
              isManualForm,
            });
            if (
              (paymentPermission?.ach_gateway_name?.name ===
                PaymentGateway.STRIPE ||
                check) &&
              bankRef.current
            ) {
              await bankRef.current.callHandleClick();
            } else {
              await handleSubmit();
            }
            break;
          case "Cash App Pay":
            if (cashAppRef.current) {
              await cashAppRef.current.callHandleClick();
            }
            break;
          case "Wire":
            await handleSubmit();
            break;
          case "Google Pay":
            await handleGooglePay(tokenData?.token, tokenData?.total_amount);
            break;
          case "PayPal": {
            const payPalUrl =
              readQuickCheckoutPayPalOrder()?.url || payPalOrder?.url;
            if (payPalUrl) window.location.href = payPalUrl;
            break;
          }
          default:
            console.log("selectedMethod =====", selectedMethod);
        }
      }
    } catch (error) {
      console.log("error =====", error);
    }
  };

  useEffect(() => {
    if (isAddNewCard) {
      setShowButtons(true);
    } else {
      if (selectedMethod === "Card") {
        if (
          paymentPermission?.cc_gateway_name?.name === PaymentGateway.STRIPE ||
          paymentPermission?.cc_gateway_name?.name === PaymentGateway.STAX
        ) {
          setShowButtons(false);
        } else {
          setShowButtons(true);
        }
      } else if (selectedMethod === "Bank") {
        let check = checkBankManualFormOrPlaid({
          manual_ach: portalSettings?.manual_ach,
          validate_bank_account: portalSettings?.validate_bank_account,
          isManualForm,
        });
        if (
          paymentPermission?.ach_gateway_name?.name === PaymentGateway.STRIPE ||
          paymentPermission?.ach_gateway_name?.name === PaymentGateway.STAX ||
          check
        ) {
          setShowButtons(false);
        } else {
          setShowButtons(true);
        }
      } else if (selectedMethod === "Wire") {
        setShowButtons(true);
      } else if (selectedMethod === "PayPal") {
        setShowButtons(false);
      } else if (selectedMethod === "Cash App Pay") {
        setShowButtons(false);
      } else if (selectedMethod === "Amazon Pay") {
        setShowButtons(false);
      }
    }
  }, [
    selectedMethod,
    isAddNewCard,
    paymentPermission,
    portalSettings,
    isManualForm,
  ]);

  // conv fee
  useEffect(() => {
    const getMethod = () => {
      if (isAddNewCard) {
        let method = "CC";
        if (
          dataPayment?.selectedBank &&
          Object.keys(dataPayment?.selectedBank).length > 0
        ) {
          method = "ACH";
        }
        return method;
      } else {
        return selectedMethod === "Card"
          ? "CC"
          : selectedMethod === "Bank"
            ? "ACH"
            : selectedMethod === "Cash App Pay"
              ? "CAPP"
              : selectedMethod === "Amazon Pay"
                ? "AMZP"
                : selectedMethod === "PayPal"
                  ? "PP"
                  : selectedMethod === "Wire"
                    ? "WT"
                    : undefined;
      }
    };

    const method = getMethod();

    let pm_id = "";

    if (method === "CC" && isAddNewCard) {
      pm_id = dataPayment?.selectedCard?.cc_token;
    } else if (method === "ACH" && isAddNewCard) {
      pm_id = dataPayment?.selectedBank?.bank_token;
    }

    const getConvFee = async () => {
      let productSlug = linkSlugParts;
      try {
        let params = {
          cluster_id: productSlug[1],
          state:
            method === "CC" && !isAddNewCard && cardFeeState
              ? cardFeeState
              : cardState.values?.x_state !== ""
                ? cardState.values?.x_state
                : userData?.data?.accountData?.state,
          x_method: method,
          phone_number: userData?.data?.accountData?.phone,
          x_amount: amount,
          lead_id: accountLeadData?.lead_id,
          pm_id,
          custom_conv: QUICK_CHECKOUT_CUSTOM_CONV,
        };

        await dispatch(convFeeGet(params)).unwrap();
      } catch (error) {
        console.log("getConvFee error =====", error);
      } finally {
        setIsFeeUpdating(false);
      }
    };
    // PayPal (and wire) carry no convenience fee: a fee kept from another
    // method must not show in the amount or the disclosures.
    if (selectedMethod === "Wire" || selectedMethod === "PayPal") {
      dispatch(resetConvFee());
      setIsFeeUpdating(false);
      return;
    }
    if (amount > 0) {
      // The quick checkout amount is typed, so wait for the customer to pause.
      if (!isValidQuickCheckoutAmount(amount)) {
        setIsFeeUpdating(false);
        return;
      }
      // Pay waits until the fee shown is the fee for this amount and method.
      setIsFeeUpdating(true);
      const feeTimer = setTimeout(getConvFee, 400);
      return () => clearTimeout(feeTimer);
    }
    setIsFeeUpdating(false);
  }, [amount, selectedMethod, isAddNewCard, dataPayment, cardFeeState]);

  useEffect(() => {
    if (hasStaleLogin) return;
    if (userData?.data?.accountData?.email) {
      paymentProcess.setEmail(userData?.data?.accountData?.email || "");
    }

    if (savedCardsEmail) {
      paymentProcess.setEmail(savedCardsEmail);
    }
  }, [userData, savedCardsEmail]);

  //drawer
  useEffect(() => {
    const updateMaxHeight = () => {
      if (drawerRef.current && !productLinkData.isLoading) {
        drawerRef.current.style.maxHeight = showDetails
          ? `${drawerRef.current.scrollHeight}px`
          : "0";
      }
    };
    updateMaxHeight();
    window.addEventListener("resize", updateMaxHeight);
    return () => {
      window.removeEventListener("resize", updateMaxHeight);
    };
  }, [showDetails, productLinkData.isLoading]);

  const generalMarkers = getGeneralDetailMarkers({
    conFee,
    paymentAmount: productLinkData.editVal?.toString(),
  });

  const handlePlaidBankPayment = async (
    value: any,
    isCCTokenAvailable: boolean
  ): Promise<void> => {
    try {
      let payload = {
        x_payment_method: "ACH",
        email: value?.email,
        x_card_num: value?.x_card_num?.replaceAll(" ", ""),
        x_card_code: value?.x_card_code,
        x_exp_date: value?.x_exp_date,
        iconType: cardState.iconType,
        routing_number: value?.routing_number,
        account_number: value?.account_number,
        bankName: value?.bankName,
        bank: value?.bank,
        x_city: value?.x_city || userData?.data?.accountData?.city || "",
        x_address:
          value?.x_address || userData?.data?.accountData?.address1 || "",
        address2:
          value?.address2 || userData?.data?.accountData?.address2 || "",
        x_zip: value?.x_zip || userData?.data?.accountData?.postal_code || "",
        x_state: value?.x_state || userData?.data?.accountData?.state || "",
        x_country:
          value?.x_country || userData?.data?.accountData?.country || "",
        choldername: value?.choldername,
        cppid: value?.cppid,
        cc_token: value?.cc_token,
        cc_token2: value?.cc_token2 ?? value?.cc_token,
        stripe_token: value?.cc_token,
        pc_atid: value?.pc_atid,
        use_old_card: isCCTokenAvailable ? "1" : "0",
      };
      await handleCardBankPayment(payload);
    } catch (error) {
      console.log("error =====", error);
    } finally {
      await dispatch(onSetFinalDisclosureLoading(false));
      await dispatch(onSetShowDisclosure(false));
    }
  };

  useEffect(() => {
    if (userData?.data?.accountData?.accountid) {
      paymentProcess.setAccountId(userData?.data?.accountData?.accountid);
    }
  }, [userData?.data?.accountData?.accountid]);

  // The final disclosure applies to everyone who pays, logged in or not; the
  // Stripe and PayPal forms already ask for it on the same setting.
  const needsFinalDisclosure =
    portalSettings?.payment_agreement_opt === SettingsValue.CUSTOM ||
    portalSettings?.payment_agreement_opt === SettingsValue.DEFAULT;

  const canEditAmount =
    !productLinkData.isLoading &&
    !isScreenLoading &&
    !paymentProcess.paymentProcess &&
    !showFinalDisclosure;

  const startAmountEdit = () => {
    if (!canEditAmount) return;
    const current = isValidQuickCheckoutAmount(amount) ? String(amount) : "";
    setAmountDraft(current);
    amountDraftRef.current = current;
    setAmountErr(null);
    setIsAmountEdit(true);
  };

  // Saves the typed amount and closes the input; an invalid amount keeps the
  // input open with an error, as in make payment.
  const saveAmount = (value: string) => {
    if (!isAmountEdit) return;
    const cleaned = String(value ?? "")
      .replaceAll(",", "")
      .replace("$", "")
      .trim();
    if (!isValidQuickCheckoutAmount(cleaned)) {
      setAmountErr("Enter an amount of at least $1.00");
      return;
    }
    const saved = Number(cleaned).toFixed(2);
    setAmountErr(null);
    setAmount(saved);
    productLinkData.setEditVal(Number(saved));
    setIsAmountEdit(false);
  };

  const handleAmountOutsideClick = () => saveAmount(amountDraftRef.current);

  // The payment form is usable before an amount is saved, but wallet buttons
  // (Google Pay, PayPal) start a payment without the Pay button. Stop them and
  // point the customer at the amount instead of charging $0.00.
  const ensureAmountEntered = (): boolean => {
    if (isValidQuickCheckoutAmount(amount)) return true;
    startAmountEdit();
    setAmountErr("Enter an amount of at least $1.00");
    return false;
  };

  // Return URL for redirect payments (Cash App, Amazon Pay, Stripe bank).
  // Built from the path so an existing query string is not repeated. The
  // email stays out of it (see rememberQuickCheckoutReturnEmail).
  const paramsURL = buildQuickCheckoutReturnUrl({ amount });

  return (
    <div
      className={` w-full lg:h-full ${productLinkData?.isProdLinkActive ? "lg:grid grid-cols-2 lg:overflow-y-auto overflow-y-scroll" : "flex justify-center items-center h-[var(--shell-vh)]"} `}
      {...testAttrs("page", "Product payment link")}
    >
      {productLinkData?.isProdLinkActive ? (
        <>
          {/* PAYMENT DESCRIPTION PART */}
          <div
            style={{ backgroundColor: brandColor }}
            className="relative lg:h-full lg:max-h-[var(--shell-vh)] lg:overflow-y-scroll lg:py-[90px] py-20 pb-5 md:px-10 px-5"
            {...testAttrs("details-panel", "Product payment details")}
          >
            <div className={` w-full lg:h-full`}>
              <div className="flex justify-center lg:mt-[7%] h-fit lg:pb-16">
                <div
                  className={`flex flex-col items-start lg:gap-10 gap-5 w-full lg:max-w-[380px]`}
                >
                  <div className="lg:flex hidden items-center gap-[10px]">
                    {productLinkData.isLoading ? (
                      <Skeleton width={100} height={35} />
                    ) : (
                      <div className="h-16 lg:h-16 flex justify-end">
                        <img
                          src={paymentLogo}
                          alt="Company logo"
                          className="animate__animated animate__fadeIn object-contain"
                        />
                      </div>
                    )}
                  </div>
                  {/* PAYMENT TYPE AND AMOUNT */}
                  <div className={`flex flex-col items-start w-full`}>
                    <span className="w-full flex justify-between items-start">
                      <div
                        className={`${
                          isDark
                            ? "!text-darktxtParagraph"
                            : "!text-txtParagraph"
                        } font-bold lg:text-xl text-lg leading-[26.04px] lg:w-[95%] w-[70%] break-words`}
                      >
                        {productLinkData.isLoading ? (
                          <Skeleton width={300} height={25} />
                        ) : (
                          productLinkData.title
                        )}
                      </div>
                    </span>
                      <div className="w-full lg:mt-4 mt-6">
                        {/* Same behaviour as make payment: amount + edit icon,
                            input while editing, saved on outside click. */}
                        <OutsideClickHandler
                          onOutsideClick={handleAmountOutsideClick}
                          className="w-fit"
                        >
                          <div className="flex items-end gap-3 cursor-pointer">
                            {isAmountEdit ? (
                              <div className="w-[266px] max-w-full relative">
                                <CustomAmountInput
                                  checkAmt={false}
                                  isCheckAllowed={false}
                                  inputValue={amountDraft}
                                  setinputValue={(value: any) => {
                                    setAmountErr(null);
                                    setAmountDraft(value);
                                    amountDraftRef.current = value;
                                  }}
                                  onBlur={(
                                    event: React.FocusEvent<HTMLInputElement>
                                  ) => saveAmount(event.target.value)}
                                  autoFocus
                                  inputMode="decimal"
                                  aria-label="Payment amount"
                                  {...testAttrs(
                                    "amount-input",
                                    "Payment amount"
                                  )}
                                  className={`lg:!text-[50px] !text-[32px] lg:!leading-[65.1px] leading-[41.66px] font-bold text-txtPrimary lg:!h-[65px] !h-[42px] lg:!pl-[45px] !pl-[32px] ${
                                    amountErr
                                      ? "!shadow-[0_0_4px_0px_rgba(255,0,0,1)]"
                                      : "!shadow-[0_0_4px_0px_rgba(22,120,242,1)]"
                                  }`}
                                  _className="lg:!text-[50px] !text-[32px] lg:!leading-[65.1px] leading-[41.66px] font-bold text-txtPrimary !top-0  left-[12px]"
                                />
                                {amountErr && (
                                  <p className="pt-4 text-red text-sm leading-[18.23px]">
                                    {amountErr}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <>
                                <p
                                  className={`font-bold lg:leading-[65.1px] leading-[41.66px] ${
                                    isDark
                                      ? "text-darkTxtPrimary"
                                      : "text-txtPrimary"
                                  } lg:text-[50px] text-[32px]`}
                                  onClick={startAmountEdit}
                                  {...testAttrs("amount", "Payment amount")}
                                >
                                  {productLinkData.isLoading ? (
                                    <Skeleton width={150} height={59} />
                                  ) : (
                                    formatCurrency(amount) || "$0.00"
                                  )}
                                </p>
                                {canEditAmount && (
                                  <div
                                    className="lg:pb-[15px] pb-3"
                                    onClick={startAmountEdit}
                                    role="button"
                                    tabIndex={0}
                                    aria-label="Edit payment amount"
                                    {...testAttrs(
                                      "edit-amount",
                                      "Edit payment amount"
                                    )}
                                  >
                                    <EditIcon
                                      strokeColor={
                                        isDark
                                          ? "var(--color-icon-dark-bright)"
                                          : "var(--color-icon-bright)"
                                      }
                                      className="group "
                                      _className={
                                        isDark
                                          ? "group-hover:!fill-darkTxtPrimary"
                                          : "group-hover:!fill-iconObscure"
                                      }
                                    />
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </OutsideClickHandler>
                      </div>
                    {productLinkData.subtitle && !productLinkData.isLoading && (
                      <p
                        className={`${
                          isDark
                            ? "!text-darktxtParagraph"
                            : "!text-txtParagraph"
                        } text-sm  leading-[20px]`}
                      >
                        {productLinkData.subtitle}
                      </p>
                    )}
                  </div>
                  <Footer
                    style={{ backgroundColor: brandColor }}
                    className={`!justify-start xl:!w-full !w-[42.5%] lg:max-w-[380px] lg:flex hidden xl:flex-nowrap flex-wrap ${
                      isDark ? "!text-darktxtParagraph" : "!text-txtParagraph"
                    }`}
                  />
                </div>
              </div>
            </div>
          </div>
          {/* PAYMENT METHOD PART */}
          <div
            ref={stepperAnchorRef}
            className="h-0 lg:hidden block"
            style={{ scrollMarginTop: "84px" }}
            {...testAttrs("payment-stepper-anchor")}
          />

          <div
            className={`text-xl font-medium w-full lg:h-full h-fit ${showFinalDisclosure ? "" : "lg:pt-[100px]"} lg:overflow-y-scroll`}
            {...testAttrs("checkout-panel", "Product checkout")}
          >
            {isScreenLoading || productLinkData.isLoading ? (
              <div
                className="w-full h-full flex justify-center items-center"
                {...testAttrs("loading", "Loading product checkout")}
              >
                <div
                  className="h-10 w-10 animate-spin rounded-full border-2 border-solid border-blue border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"
                  aria-hidden="true"
                ></div>
              </div>
            ) : paymentProcess.paymentProcess ? (
              paymentProcess.paymentProcess === "true" ? (
                paymentProcess.isWirePayment ? (
                  <div
                    className="h-full w-full grid place-items-center"
                    {...testAttrs("wire-payment-result", "Wire payment result")}
                  >
                    <WirePaymentSuccess
                      bankDetails={bankState.wireDetails}
                      orderId={paymentProcess.orderId}
                      isInvoice={paymentProcess.invoiceSlug ? true : false}
                      invoiceNumber={paymentProcess.invoiceNumber}
                      invoiceSlug={paymentProcess.invoiceSlug}
                      sendEmail={sendEmail}
                      lead_id={paymentProcess.leadId}
                    />
                  </div>
                ) : (
                  <div
                    className="h-full w-full grid place-items-center"
                    {...testAttrs("payment-result", "Product payment result")}
                  >
                    <ProductLinkSuccess
                      email={paymentProcess.email}
                      amount={paymentProcess.amountDisplay}
                      orderId={paymentProcess.orderId}
                      invoiceSlug={paymentProcess.invoiceSlug}
                      sendEmail={sendEmail}
                      isRecurring={isRecurring}
                      clusterId={linkClusterId}
                      lead_id={paymentProcess.leadId}
                      invoiceNumber={paymentProcess.invoiceNumber}
                      accountId={paymentProcess.accountId}
                      showSuccessToast
                    />
                  </div>
                )
              ) : paymentProcess.paymentProcess === "pending" ? (
                <QuickCheckoutPending message={paymentErrors.failedReason} />
              ) : (
                <div {...testAttrs("payment-failed", "Product payment failed")}>
                  <PaymentLinkProcessFailed
                    failedReason={paymentErrors.failedReason}
                    handleBackToStart={handleBackToStart}
                  />
                </div>
              )
            ) : portalSettings?.state_disclosure_flag === SettingsValue.OFF ||
              initialDisclosureAccept ? (
              <div
                className={`${
                  showFinalDisclosure
                    ? "h-full"
                    : "px-5 grid place-items-center mt-2  w-full mb-3"
                } `}
                {...testAttrs("payment-form-section", "Product payment form")}
              >
                <div
                  className={`${
                    showFinalDisclosure ? "hidden none" : "flex"
                  } flex-col lg:max-w-[510px] lg:gap-10 gap-5  text-LightTextParagraph lg:!w-full`}
                >
                  {isAddNewCard ? (
                    <>
                      <div
                        {...testAttrs(
                          "saved-payment-method-section",
                          "Saved payment method"
                        )}
                      >
                        <div className="flex justify-between items-center mb-5">
                          <p className="lg:text-xl text-base font-medium text-txtPrimary">
                            Payment method
                          </p>

                          <p
                            onClick={() => {
                              setIsAddNewCard(false);
                            }}
                            className="flex gap-2 items-center cursor-pointer xl:text-sm text-xs  font-medium text-blue hover:!text-btnPrimaryHover"
                            role="button"
                            tabIndex={0}
                            {...testAttrs(
                              "add-new-payment-method",
                              "Add new payment method"
                            )}
                          >
                            <AddCircleIconBlue aria-hidden="true" /> Add New
                          </p>
                        </div>

                        <div
                          {...testAttrs(
                            "saved-payment-method-form",
                            "Saved payment method form"
                          )}
                        >
                          <PaymentProcessStep2
                            data={dataPayment}
                            setData={setDataPayment}
                            setCurrentStep={() => {}}
                            isDataLoading={isDataLoading}
                            currentStep={2}
                            walletList={walletList}
                            setSavedCardsEmail={setSavedCardsEmail}
                            isPaymentLink={true}
                            paymentMethodList={paymentMethodList}
                            setSelectedMethod={setSelectedMethod}
                            collectPhone={
                              productLinkData.prodPermission.collect_phone ===
                              "Y"
                            }
                            phoneNumber={productLinkData?.phoneNumber}
                            setPhoneNumber={productLinkData?.setPhoneNumber}
                            handleEmailBlur={handleEmailBlur}
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div
                      {...testAttrs(
                        "payment-method-form",
                        "Product payment method form"
                      )}
                    >
                      <PaymentMethodsComponent
                        // A saved amount change must not reuse a Plaid link
                        // or Stripe intent created for the old amount
                        // (both keep their first amount), so the form is
                        // remounted once such a method has been opened (see
                        // paymentForm). Card/bank fields typed into page
                        // state stay.
                        key={`payment-form-${paymentForm.version}`}
                        cardData={{
                          cardNumber: cardState.cardNumber,
                          isDateErr: cardState.isDateErr,
                          isErr: cardState.isErr,
                          cardErrMsg: cardState.cardErrMsg,
                          handleChangeCard,
                          validateCard: () =>
                            validateCard({
                              values: cardState.values,
                              iconType: cardState.iconType,
                              setIsErr: cardState.setIsErr,
                              setCardErrMsg: cardState.setCardErrMsg,
                              setErrMsg: paymentErrors.setErrMsg,
                            }),
                          iconType: cardState.iconType,
                          setIconType: cardState.setIconType,
                          setIsAddNewCardMakePayment: setIsAddNewCard,
                          isAddNewCardMakePayment: isAddNewCard,
                          showNewStripeCardForm: true,
                        }}
                        bankData={{
                          bankName: bankState.bankName,
                          numberNotmatchedError:
                            paymentErrors.numberNotmatchedError,
                          setnumberNotmatchedError:
                            paymentErrors.setnumberNotmatchedError,
                          handleChangeBank,
                          selectedState,
                          setSelectedState,
                          stripeBankURL: `${paramsURL}&method=bank`,
                          isManualForm,
                          setIsManualForm,
                          handlePlaidBankPayment,
                        }}
                        paymentConfig={{
                          paymentMethodList,
                          selectedMethod,
                          setSelectedMethod,
                          amount,
                          isPaymentLink: true,
                          isProductLink: true,
                          loadBankFormBeforeEmail: true,
                          custom_conv: QUICK_CHECKOUT_CUSTOM_CONV,
                          isStaxPayPalLoading,
                          handleEmailBlur,
                        }}
                        paymentHandlers={{
                          handleGooglePay: (tokenData: any, amount: any) => {
                            if (!needsFinalDisclosure) {
                              handleGooglePay(tokenData, amount);
                            } else {
                              setSelectedMethod("Google Pay");
                              setTokenData({
                                token: tokenData,
                                total_amount: amount,
                              });
                              dispatch(onSetShowDisclosure(true));
                            }
                          },
                          createPayPalIntent,
                          handleApplePay,
                          verifyStripeCardPayment,
                          verifyStaxCardBank,
                          verifyStaxPayPal,
                        }}
                        paymentUrls={{
                          cashAppURL: `${paramsURL}&method=cashapp`,
                          stripeAmazonPayURL: `${paramsURL}&method=amazonPay`,
                        }}
                        formState={{
                          values: cardState.values,
                          email: paymentProcess.email,
                          setEmail: paymentProcess.setEmail,
                          errMsg: paymentErrors.errMsg,
                          isRingLoading,
                        }}
                        paymentGatewayState={{
                          isGoogleLoading,
                        }}
                        componentRefs={{
                          cashAppRef,
                          bankRef,
                          cardRef,
                          amazonRef,
                        }}
                        phoneAndAddressConfig={{
                          collectAddress:
                            productLinkData.prodPermission.collect_address ===
                            "Y",
                          collectPhone:
                            productLinkData.prodPermission.collect_phone ===
                            "Y",
                          phoneNumber: productLinkData?.phoneNumber,
                          setPhoneNumber: productLinkData?.setPhoneNumber,
                        }}
                        noAuthClusterId={linkClusterId}
                      />
                    </div>
                  )}

                  <div className="w-full">
                    {showButtons && (
                      <>
                        <Button
                          text={`
                      Pay ${
                        formatCurrency(parseFloat(amount) + conFee) || "$0.00"
                      }
                    `}
                          className={`w-full`}
                          isDisabled={
                            isDisabled ||
                            isFeeUpdating ||
                            !isValidQuickCheckoutAmount(amount) ||
                            (selectedMethod === "Bank" &&
                              !isAddNewCard &&
                              (isRingLoading ||
                                paymentErrors.numberNotmatchedError))
                          }
                          isLoading={isBTNLoading}
                          {...testAttrs(
                            "submit-payment",
                            `Pay ${formatCurrency(parseFloat(amount) + conFee) || "$0.00"}`
                          )}
                          onClick={() => {
                            if (!needsFinalDisclosure) {
                              handleSubmit();
                            } else {
                              dispatch(onSetShowDisclosure(true));
                            }
                          }}
                        />
                      </>
                    )}
                  </div>
                </div>
                {showFinalDisclosure && (
                  <div
                    className={`h-full lg:max-h-[var(--shell-vh)] relative content w-full lg:pt-20 lg:pb-[40px] lg:px-10 md:p-10 p-5 max-w-[740px] mx-auto text-base content ${
                      !showFinalDisclosure ? `hidden none` : ""
                    }`}
                    {...testAttrs(
                      "final-disclosure",
                      "Final product payment disclosure"
                    )}
                  >
                    <DisclosureFinal
                      isOpen={showFinalDisclosure}
                      generalMarkers={generalMarkers}
                      handleAccept={handleAccept}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div
                className="xl:px-20 lg:px-10 px-5 h-full w-full lg:pt-0 pt-5"
                {...testAttrs(
                  "initial-disclosure",
                  "Initial product payment disclosure"
                )}
              >
                <DisclosureInitial
                  isOpen={true}
                  handleAccept={() => setInitialDisclosureAccept(true)}
                  generalMarkers={generalMarkers}
                />
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div
            className="h-fit w-fit p-5"
            {...testAttrs("inactive-link", "Product link inactive")}
          >
            <div className="flex flex-col gap-10 justify-center items-center">
              <BigInfoCircle aria-hidden="true" />
              <p className="lg:text-2xl text-lg text-txtPrimary font-medium leading-normal">
                The link is no longer active
              </p>
            </div>
          </div>
          <Footer
            className={` ${
              isSmallSidebar
                ? "lg:!w-[calc(var(--shell-vw)-72px)] lg:left-[72px]"
                : "lg:!w-[calc(var(--shell-vw)-254px)] lg:left-[254px]"
            } lg:px-0 px-5 !text-txtParagraph transition-all duration-300 ease-in-out`}
          />
        </>
      )}
    </div>
  );
};

const QuickCheckout = () => (
  <TestIdScope scope="quick-checkout">
    <QuickCheckoutContent />
  </TestIdScope>
);

export default QuickCheckout;
